import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Editor } from '@tinymce/tinymce-react';

// TinyMCE will be loaded from CDN
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { useFormHandler } from '../../../utils/useFormHandler';
import { emailTemplateValidationSchema } from '../../../utils/validationSchemas';
import {
  registerEmailTemplate,
  updateEmailTemplate,
  fetchEmailTemplateByIdFromAPI,
} from '../../../services/EmailTemplateService';

const AddEmailTemplate = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const editorRef = useRef(null);

  const emailTemplate = state?.emailTemplate;
  const isEditing = emailTemplate && emailTemplate.id;

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Preview states
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({});
  
  // Tab states
  const [activeTab, setActiveTab] = useState(0);

  const apiFunction = isEditing ? updateEmailTemplate : registerEmailTemplate;

  // Success Callback
  const onSuccess = () => {
    setSnackbarMessage(isEditing ? 'Email template updated successfully!' : 'Email template created successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);

    setTimeout(() => {
      navigate('/managements/email-templates');
    }, 1000);
  };

  // Error Callback
  const onError = (error) => {
    console.error('Email template error:', error);
    setSnackbarMessage(error.message || 'An error occurred');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  };

  const formik = useFormHandler(
    {
      template_name: emailTemplate?.template_name || '',
      template_type: emailTemplate?.template_type || '',
      subject: emailTemplate?.subject || '',
      body: emailTemplate?.body || '',
      variables: emailTemplate?.variables || {},
      is_active: emailTemplate?.is_active !== undefined ? emailTemplate.is_active : true,
      description: emailTemplate?.description || ''
    },
    emailTemplateValidationSchema,
    apiFunction,
    onSuccess,
    onError,
    isEditing ? emailTemplate.id : null
  );

  // Preview handler
  const handlePreview = () => {
    setPreviewData({
      subject: formik.values.subject,
      body: formik.values.body
    });
    setShowPreview(true);
  };

  return (
    <Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <form onSubmit={(e) => {
        console.log('Form submitted with values:', formik.values);
        formik.handleSubmit(e);
      }}>
        <Grid container spacing={3}>
          {/* Template Name */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Template Name *
            </Typography>
            <TextField
              id="template_name"
              fullWidth
              placeholder="e.g., Welcome Email Template"
              {...formik.getFieldProps('template_name')}
              error={formik.touched.template_name && Boolean(formik.errors.template_name)}
              helperText={formik.touched.template_name && formik.errors.template_name}
            />
          </Grid>

          {/* Template Type */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Template Type *
            </Typography>
            <TextField
              id="template_type"
              fullWidth
              placeholder="e.g., welcome, notification, reminder"
              {...formik.getFieldProps('template_type')}
              error={formik.touched.template_type && Boolean(formik.errors.template_type)}
              helperText={formik.touched.template_type && formik.errors.template_type}
            />
          </Grid>

          {/* Subject */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Subject *
            </Typography>
            <TextField
              id="subject"
              fullWidth
              placeholder="e.g., Welcome to {`{{company_name}}`}, {`{{user_name}}`}!"
              {...formik.getFieldProps('subject')}
              error={formik.touched.subject && Boolean(formik.errors.subject)}
              helperText={formik.touched.subject && formik.errors.subject}
            />
          </Grid>

          {/* Body Template */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Body Template *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Preview Email">
                  <IconButton onClick={handlePreview} color="primary">
                    <PreviewIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Markdown Help">
                  <IconButton color="info">
                    <CodeIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Rich Text Editor */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Use the rich text editor below to create your email template. Use {`{{variable_name}}`} for dynamic content.
              </Typography>
              
              <Editor
                onInit={(evt, editor) => editorRef.current = editor}
                value={formik.values.body}
                onEditorChange={(content) => {
                  formik.setFieldValue('body', content);
                  formik.setFieldTouched('body', true);
                }}
                onBlur={() => formik.setFieldTouched('body', true)}
                init={{
                  height: 400,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                  placeholder: 'Start typing your email template here... Use {{user_name}}, {{company_name}}, etc. for dynamic content.',
                  setup: function (editor) {
                    editor.on('init', function () {
                      if (!formik.values.body) {
                        editor.setContent('<h1>Welcome {{user_name}}!</h1><p>Thank you for joining <strong>{{company_name}}</strong>.</p><h2>What\'s Next?</h2><ul><li>Complete your profile</li><li>Explore our features</li><li>Contact support if needed</li></ul><p>Best regards,<br>The Team</p>');
                      }
                    });
                  }
                }}
                apiKey="i1c09el6z4c1d6ak9vooa10usl56honf15v8iux2aqsshicx"
              />
              
              {formik.touched.body && formik.errors.body && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {formik.errors.body}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Variables */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SettingsIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Available Variables
                  </Typography>
                  <Tooltip title="Variables that can be used in templates with {`{{variable_name}}`} syntax">
                    <IconButton size="small">
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define variables that can be used in your email templates. Use the format: {`{{variable_name}}`}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder='{"user_name": "John Doe", "company_name": "Acme Corp", "dashboard_url": "https://app.example.com"}'
                  value={typeof formik.values.variables === 'object' ? JSON.stringify(formik.values.variables, null, 2) : formik.values.variables || ''}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      formik.setFieldValue('variables', parsed);
                    } catch {
                      formik.setFieldValue('variables', e.target.value);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('variables', true)}
                  error={formik.touched.variables && Boolean(formik.errors.variables)}
                  helperText={formik.touched.variables && formik.errors.variables}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Active Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.is_active}
                  onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Description
            </Typography>
            <TextField
              id="description"
              fullWidth
              multiline
              rows={3}
              placeholder="Optional description for this email template..."
              {...formik.getFieldProps('description')}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box mt={3} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/managements/email-templates')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Saving...' : (isEditing ? 'Update Template' : 'Create Template')}
          </Button>
        </Box>
      </form>

      {/* Preview Dialog */}
      {showPreview && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowPreview(false)}
        >
          <Card sx={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Email Preview</Typography>
                <Button onClick={() => setShowPreview(false)}>Close</Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" sx={{ mb: 2, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <strong>Subject:</strong> {previewData.subject}
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: 'white'
                }}
                dangerouslySetInnerHTML={{ __html: previewData.body }}
              />
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default AddEmailTemplate;
